/**
 * Drawer section 2 (#1911) — WHO this reaches.
 *
 * ── A STRATEGY PICKER OVER ONE STRATEGY, ON PURPOSE ─────────────────────────
 * v1 has exactly one way of drawing an audience, and this still renders a
 * dropdown above a parameter block rather than putting a model list and a
 * lookback box straight into the form. Selecting by model is ONE policy — it
 * is not what a notice IS — and #1877 proved what happens when the two are
 * flattened together: the audience, the mail's headline and a catalog mutation
 * all became one field, and every change to one of them was a change to the
 * other two.
 *
 * The shape here is the union's shape. A second member costs a `case` in
 * `emptyAudienceFor`, a `case` in `toAudience` and a branch in `Parameters`
 * below — no field moves, no caller changes, and the dropdown grows an option
 * because it is rendered from `NOTICE_AUDIENCE_TYPES` rather than from a
 * literal.
 *
 * ── NO SHELF FILTER ─────────────────────────────────────────────────────────
 * #1877 removed models from this picker whose shelf status disagreed with the
 * first pick, because the notice's kind was inferred from it. There is no kind
 * now: an operator can announce anything about any combination of models, so
 * every live model is selectable and a "mixed" selection is an ordinary one.
 *
 * ── THE COUNT IS ANSWERED HERE, NOT IN SECTION 3 (#1920) ────────────────────
 * "How many accounts is this" is a property of the audience, so it is asked and
 * answered beside the audience — before a title, a body or a saved draft exist.
 * Section 3 stays what it was: the pre-send gate, where a list gets frozen.
 */
import type { ModelRecord, NoticeAudienceType } from '@nadoutong/contracts';
import { EMAIL_NOTICE_MAX_WINDOW_DAYS, NOTICE_AUDIENCE_TYPES } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { FormField, Input, Select } from '../../ui/index.ts';
import { SectionCaption } from './SectionCaption.tsx';
import { AudiencePreviewPanel, type AudiencePreviewControls } from './AudiencePreviewPanel.tsx';
import { ModelPicker } from './ModelPicker.tsx';
import { audienceLabel } from './noticeStatus.ts';
import { type AudienceDraft, emptyAudienceFor } from './noticeDraft.ts';

interface Props {
  readonly audience: AudienceDraft;
  readonly onChange: (next: AudienceDraft) => void;
  readonly models: readonly ModelRecord[];
  readonly modelsLoading: boolean;
  readonly disabled: boolean;
  readonly preview: AudiencePreviewControls;
}

/** `Select` hands back a bare string; narrow it rather than asserting. */
function toAudienceType(value: string, fallback: NoticeAudienceType): NoticeAudienceType {
  return NOTICE_AUDIENCE_TYPES.find((entry) => entry === value) ?? fallback;
}

export function NoticeAudienceSection({
  audience,
  onChange,
  models,
  modelsLoading,
  disabled,
  preview,
}: Props): JSX.Element {
  const t = useT();

  return (
    <section className="flex flex-col gap-2" data-testid="email-notice-section-audience">
      <SectionCaption
        index={2}
        title={t('email_notices.section.audience')}
        hint={t('email_notices.section.audience_hint')}
      />
      <FormField
        label={t('email_notices.field.audience_type')}
        fieldId="notice-audience-type"
        hint={t('email_notices.field.audience_type_hint')}
      >
        <Select
          id="notice-audience-type"
          value={audience.type}
          onValueChange={(value) => onChange(emptyAudienceFor(toAudienceType(value, audience.type)))}
          options={NOTICE_AUDIENCE_TYPES.map((type) => ({
            value: type,
            label: t(audienceLabel(type)),
          }))}
          disabled={disabled}
          testId="email-notice-audience-type"
        />
      </FormField>
      <AudienceParameters
        audience={audience}
        onChange={onChange}
        models={models}
        modelsLoading={modelsLoading}
        disabled={disabled}
      />
      <AudiencePreviewPanel preview={preview} />
    </section>
  );
}

type ParameterProps = Omit<Props, 'preview'>;

/** The parameter block for whichever strategy is selected. */
function AudienceParameters({
  audience,
  onChange,
  models,
  modelsLoading,
  disabled,
}: ParameterProps): JSX.Element {
  switch (audience.type) {
    case 'models_used':
      return (
        <ModelsUsedParameters
          audience={audience}
          onChange={onChange}
          models={models}
          modelsLoading={modelsLoading}
          disabled={disabled}
        />
      );
  }
}

function ModelsUsedParameters({
  audience,
  onChange,
  models,
  modelsLoading,
  disabled,
}: ParameterProps): JSX.Element {
  const t = useT();

  return (
    <div className="flex flex-col gap-2">
      <ModelPicker
        selected={audience.models}
        onChange={(next) => onChange({ ...audience, models: next })}
        models={models}
        modelsLoading={modelsLoading}
        disabled={disabled}
      />
      <FormField
        label={t('email_notices.field.window_days')}
        fieldId="notice-window-days"
        hint={t('email_notices.field.window_days_hint', {
          max: String(EMAIL_NOTICE_MAX_WINDOW_DAYS),
        })}
      >
        <Input
          id="notice-window-days"
          type="number"
          mono
          min={1}
          max={EMAIL_NOTICE_MAX_WINDOW_DAYS}
          value={audience.windowDays}
          disabled={disabled}
          onChange={(event) => onChange({ ...audience, windowDays: event.target.value })}
          data-testid="email-notice-window-days"
        />
      </FormField>
    </div>
  );
}
